import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, put, del } from "../services/apiClient";
import type { Medicine, BusinessMedicine } from "@postnatal-care/shared-types";

// Master Medicines
export const useGlobalMedicines = () => {
  return useQuery<Medicine[]>({
    queryKey: ["medicines", "global"],
    queryFn: () => get("/medicines/global"),
  });
};

export const useCreateGlobalMedicine = () => {
  const queryClient = useQueryClient();
  return useMutation<
    Medicine,
    Error,
    Omit<Medicine, "id" | "createdAt" | "updatedAt">
  >({
    mutationFn: (data) => post("/medicines/global", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["medicines", "global"] });
    },
  });
};

export const useUpdateGlobalMedicine = () => {
  const queryClient = useQueryClient();
  return useMutation<Medicine, Error, { id: string; data: Partial<Medicine> }>({
    mutationFn: ({ id, data }) => put(`/medicines/global/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["medicines", "global"] });
    },
  });
};

export const useDeleteGlobalMedicine = () => {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: (id) => del(`/medicines/global/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["medicines", "global"] });
    },
  });
};

// Linked Business Medicines
export const useBusinessMedicines = (businessId: string) => {
  return useQuery<BusinessMedicine[]>({
    queryKey: ["medicines", "business", businessId],
    queryFn: () => get(`/medicines/business/${businessId}`),
    enabled: !!businessId,
  });
};

export const useLinkMedicine = (businessId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    BusinessMedicine,
    Error,
    {
      medicineId: string;
      dosageInstructionsEn?: string;
      dosageInstructionsMl?: string;
      frequencyEn?: string;
      frequencyMl?: string;
      isPaid?: boolean;
      price?: number;
      isActive?: boolean;
    }
  >({
    mutationFn: (data) => post(`/medicines/business/${businessId}/link`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["medicines", "business", businessId],
      });
    },
  });
};

export const useUpdateBusinessMedicine = (businessId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    BusinessMedicine,
    Error,
    {
      linkId: string;
      data: {
        dosageInstructionsEn?: string;
        dosageInstructionsMl?: string;
        frequencyEn?: string;
        frequencyMl?: string;
        isPaid?: boolean;
        price?: number;
        isActive?: boolean;
      };
    }
  >({
    mutationFn: ({ linkId, data }) =>
      put(`/medicines/business/${businessId}/link/${linkId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["medicines", "business", businessId],
      });
    },
  });
};

export const useUnlinkMedicine = (businessId: string) => {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: (linkId) =>
      del(`/medicines/business/${businessId}/link/${linkId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["medicines", "business", businessId],
      });
    },
  });
};
